import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import {
  Clock,
  User,
  Edit,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Code,
  Calendar,
  Activity,
  FileText,
  Plus,
  Trash2,
  Palette,
  Rocket,
  Save,
  BarChart3,
  RefreshCw,
  Download,
  Users,
  Globe,
  TrendingUp,
  X,
  CheckCircle,
  AlertCircle,
  Info,
  Zap,
  Monitor,
  Hash,
  Target,
  Settings,
  ChevronDown,
  MoreHorizontal
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import LoadingSpinner from '../common/LoadingSpinner'
import ModernCalendar from '../ui/ModernCalendar'

// ── Helpers ───────────────────────────────────────────────────────────────────

// Local label map — fallback when the backend metadata doesn't include a type
const EDIT_TYPE_LABELS = {
  text_change:      'Text Change',
  new_page:         'New Page',
  visual_edit:      'Visual Edit',
  component_add:    'Component Added',
  component_delete: 'Component Deleted',
  style_change:     'Style Change',
  publish:          'Published',
  save_draft:       'Draft Saved',
  media_upload:     'Media Upload',
  settings_change:  'Settings Change',
  domain_add:       'Domain Added',
  domain_verify:    'Domain Verified',
  domain_delete:    'Domain Removed',
  domain_update:    'Domain Updated',
}

const getEditTypeLabel = (editType, metadata) => {
  if (!editType) return 'Other'
  return (
    metadata?.editTypes?.find(t => t.value === editType)?.label ||
    EDIT_TYPE_LABELS[editType] ||
    editType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  )
}

const getEditTypeIcon = (editType) => {
  if (!editType) return Activity
  const icons = {
    text_change:      FileText,
    new_page:         Plus,
    visual_edit:      Edit,
    component_add:    Plus,
    component_delete: Trash2,
    style_change:     Palette,
    publish:          Rocket,
    save_draft:       Save,
    media_upload:     FileText,
    settings_change:  Settings,
    domain_add:       Globe,
    domain_verify:    Globe,
    domain_delete:    Globe,
    domain_update:    Globe,
  }
  return icons[editType] || Edit
}

// Returns inline style for icon badge background/color
const getEditTypeStyle = (editType) => {
  const styles = {
    text_change:      { background: 'rgba(146,141,171,0.15)', color: 'var(--text-muted)' },
    new_page:         { background: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
    visual_edit:      { background: 'rgba(108,99,255,0.12)', color: 'var(--accent)' },
    component_add:    { background: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
    component_delete: { background: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
    style_change:     { background: 'rgba(236,72,153,0.12)', color: '#ec4899' },
    publish:          { background: 'rgba(249,115,22,0.12)', color: '#f97316' },
    save_draft:       { background: 'rgba(146,141,171,0.1)', color: 'var(--text-muted)' },
    media_upload:     { background: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
    settings_change:  { background: 'rgba(168,85,247,0.12)', color: '#a855f7' },
    domain_add:       { background: 'rgba(6,182,212,0.12)',  color: '#06b6d4' },
    domain_verify:    { background: 'rgba(20,184,166,0.12)', color: '#14b8a6' },
    domain_delete:    { background: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
    domain_update:    { background: 'rgba(6,182,212,0.12)',  color: '#06b6d4' },
  }
  return styles[editType] || { background: 'rgba(146,141,171,0.1)', color: 'var(--text-muted)' }
}

// Returns badge color name for the design system badge variant
const getEditTypeBadgeStyle = (editType) => {
  const map = {
    new_page:         { background: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
    visual_edit:      { background: 'rgba(108,99,255,0.12)', color: 'var(--accent)' },
    component_add:    { background: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
    component_delete: { background: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
    publish:          { background: 'rgba(249,115,22,0.12)', color: '#f97316' },
    domain_delete:    { background: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
    domain_verify:    { background: 'rgba(20,184,166,0.12)', color: '#14b8a6' },
  }
  return map[editType] || { background: 'rgba(108,99,255,0.10)', color: 'var(--accent)' }
}

const formatRelativeTime = (timestamp) => {
  const diff = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  const hrs  = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1)  return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hrs  < 24) return `${hrs}h ago`
  if (days < 7)  return `${days}d ago`
  return new Date(timestamp).toLocaleString()
}

const DiffBadges = ({ diffData }) => {
  if (!diffData?.stats) return null
  const { charactersAdded, charactersRemoved } = diffData.stats
  if (!charactersAdded && !charactersRemoved) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {charactersAdded > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,0.12)' }}>
          +{charactersAdded}
        </span>
      )}
      {charactersRemoved > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#ef4444', background: 'rgba(239,68,68,0.12)' }}>
          -{charactersRemoved}
        </span>
      )}
    </div>
  )
}

// ── Pagination sub-component ──────────────────────────────────────────────────

const Pagination = ({ pagination, onPageChange, limit, onLimitChange }) => {
  if (!pagination) return null
  const { page, pages, total, limit: pgLimit } = pagination
  const from = ((page - 1) * pgLimit) + 1
  const to   = Math.min(page * pgLimit, total)
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {from}–{to} of {total}
        </span>
        {onLimitChange && (
          <select
            value={limit}
            onChange={(e) => onLimitChange(parseInt(e.target.value))}
            style={{ height: 30, padding: '0 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', color: 'var(--text)', outline: 'none' }}
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <ChevronLeft style={{ width: 15, height: 15 }} /> Prev
        </button>
        <span style={{ fontSize: 13, color: 'var(--text-muted)', padding: '0 4px' }}>
          {page} / {pages}
        </span>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page === pages}
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          Next <ChevronRight style={{ width: 15, height: 15 }} />
        </button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const EditLogsDashboard = () => {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState({
    siteId: '',
    userId: '',
    editType: '',
    action: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50,
    search: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)
  const [viewMode, setViewMode] = useState('timeline')
  const [isAutoRefresh, setIsAutoRefresh] = useState(false)

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: logsData, isLoading, isFetching, error, refetch } = useQuery(
    ['edit-logs', filters],
    () => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      return apiService.get(`/edit-logs?${params.toString()}`).then(res => res.data)
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchInterval: isAutoRefresh && !selectedLog ? 30000 : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['edit-stats', filters.siteId, filters.userId, filters.startDate, filters.endDate],
    () => {
      const params = new URLSearchParams()
      if (filters.siteId)    params.append('siteId',    filters.siteId)
      if (filters.userId)    params.append('userId',    filters.userId)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate)   params.append('endDate',   filters.endDate)
      return apiService.get(`/edit-logs/stats?${params.toString()}`).then(res => res.data)
    },
    {
      enabled: viewMode === 'stats',
      staleTime: 5 * 60 * 1000,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  const { data: metadata } = useQuery(
    'edit-metadata',
    () => apiService.get('/edit-logs/metadata').then(res => res.data),
    {
      staleTime: 300000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleCloseModal = () => setSelectedLog(null)

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  const handleLimitChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }))
  }

  const clearFilters = () => {
    setFilters({ siteId: '', userId: '', editType: '', action: '', startDate: '', endDate: '', page: 1, limit: 50, search: '' })
  }

  const exportLogs = () => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== 'page' && key !== 'limit') params.append(key, value)
    })
    window.open(`/edit-logs/export?${params.toString()}`, '_blank')
  }

  const activeFilterCount = Object.entries(filters).filter(([key, value]) =>
    value && !['page', 'limit'].includes(key)
  ).length

  // ── Sub-views ──────────────────────────────────────────────────────────────

  const TimelineView = ({ logs }) => (
    <div className="card" style={{ overflow: 'hidden' }}>
      {!logs?.length ? (
        <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-faint)' }}>
          No edit logs found.
        </div>
      ) : (
        <div>
          {logs.map((log, idx) => {
            const Icon = getEditTypeIcon(log.edit_type)
            const iconStyle = getEditTypeStyle(log.edit_type)
            const isLast = idx === logs.length - 1
            return (
              <div
                key={log.id}
                onClick={() => setSelectedLog(log)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '14px 18px', cursor: 'pointer',
                  borderBottom: isLast ? 'none' : '1px solid var(--border)',
                }}
              >
                <div style={{ ...iconStyle, flexShrink: 0, width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                  <Icon style={{ width: 15, height: 15 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.user_name}
                      </span>
                      <span style={{ ...getEditTypeBadgeStyle(log.edit_type), display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                        {getEditTypeLabel(log.edit_type, metadata)}
                      </span>
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--text-faint)', flexShrink: 0 }}>
                      {formatRelativeTime(log.created_at)}
                    </span>
                  </div>
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.5 }}>
                    {log.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-faint)' }}>
                      <Globe style={{ width: 11, height: 11 }} />
                      <span>{log.site_name}</span>
                      <span>·</span>
                      <span>{log.company_name}</span>
                    </div>
                    <DiffBadges diffData={log.diff_data} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  const ListView = ({ logs }) => (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-0)' }}>
              {['Activity', 'User', 'Site', 'Changes', 'Time'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs?.map((log) => {
              const Icon = getEditTypeIcon(log.edit_type)
              const iconStyle = getEditTypeStyle(log.edit_type)
              return (
                <tr
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                >
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ ...iconStyle, width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon style={{ width: 14, height: 14 }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getEditTypeLabel(log.edit_type, metadata)}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                          {log.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                          {log.user_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span style={{ fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.user_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.site_name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.company_name}</div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <DiffBadges diffData={log.diff_data} />
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {formatRelativeTime(log.created_at)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!logs?.length && (
        <div style={{ padding: '36px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-faint)' }}>
          No edit logs found.
        </div>
      )}
    </div>
  )

  const StatCard = ({ title, value, icon: Icon, index = 0 }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <div className="card card-pad">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{title}</p>
            <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '4px 0 0', fontFamily: 'var(--font-mono)' }}>
              {value?.toLocaleString() ?? 0}
            </p>
          </div>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon style={{ width: 18, height: 18, color: '#fff' }} />
          </div>
        </div>
      </div>
    </motion.div>
  )

  const StatsView = ({ stats }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <StatCard title="Total Edits"   value={stats?.reduce((s, x) => s + x.count, 0) || 0}          icon={Edit}    index={0} />
        <StatCard title="Active Users"  value={stats?.reduce((s, x) => s + x.unique_users, 0) || 0}   icon={Users}   index={1} />
        <StatCard title="Sites Updated" value={stats?.length || 0}                                     icon={Globe}   index={2} />
        <StatCard title="Publishes"     value={stats?.find(x => x.edit_type === 'publish')?.count || 0} icon={Rocket}  index={3} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {stats?.map((stat, index) => {
          const Icon = getEditTypeIcon(stat.edit_type)
          const iconStyle = getEditTypeStyle(stat.edit_type)
          return (
            <motion.div
              key={stat.edit_type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.06 }}
            >
              <div className="card card-pad">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ ...iconStyle, width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon style={{ width: 14, height: 14 }} />
                  </div>
                  <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                    {getEditTypeLabel(stat.edit_type, metadata)}
                  </h3>
                </div>
                <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0, fontFamily: 'var(--font-mono)' }}>{stat.count}</p>
                <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '3px 0 0' }}>
                  {stat.unique_users} users
                </p>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-faint)' }}>
                  <span>First: {formatRelativeTime(stat.first_edit)}</span>
                  <span>Last: {formatRelativeTime(stat.last_edit)}</span>
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )

  // ── Error state ────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div style={{ paddingTop: 4, textAlign: 'center', padding: '48px 0' }}>
        <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
          <AlertCircle style={{ width: 26, height: 26, color: '#ef4444' }} />
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px' }}>
          Error loading edit logs
        </h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px' }}>{error.message}</p>
        <button className="btn btn-ghost" onClick={refetch} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw style={{ width: 14, height: 14 }} />
          Retry
        </button>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 4 }}>

      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Logs</div>
          <h1>Edit Logs</h1>
          <div className="subtitle">Track all website edits and changes across your platform</div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {/* Auto-refresh */}
          <button
            className={isAutoRefresh ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={() => setIsAutoRefresh(!isAutoRefresh)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw style={{ width: 13, height: 13, animation: isFetching ? 'spin 1s linear infinite' : undefined }} />
            Auto-refresh
          </button>

          {/* Export */}
          <button className="btn btn-ghost btn-sm" onClick={exportLogs} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download style={{ width: 13, height: 13 }} />
            Export
          </button>

          {/* View switcher */}
          <div style={{ display: 'flex', background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, padding: 2 }}>
            {[
              { key: 'timeline', icon: Activity,   label: 'Timeline' },
              { key: 'list',     icon: BarChart3,  label: 'List'     },
              { key: 'stats',    icon: TrendingUp, label: 'Stats'    },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 6, fontSize: 12.5, fontWeight: 500,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  background: viewMode === key ? 'var(--surface)' : 'transparent',
                  color: viewMode === key ? 'var(--accent)' : 'var(--text-muted)',
                  boxShadow: viewMode === key ? 'var(--shadow-sm)' : 'none',
                }}
              >
                <Icon style={{ width: 13, height: 13 }} />
                {label}
              </button>
            ))}
          </div>

          {/* Filters toggle */}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setShowFilters(!showFilters)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderColor: activeFilterCount > 0 ? 'var(--accent)' : undefined }}
          >
            <Filter style={{ width: 13, height: 13 }} />
            Filters
            {activeFilterCount > 0 && (
              <span style={{ fontSize: 10.5, background: 'var(--accent)', color: '#fff', borderRadius: 999, padding: '1px 6px', fontWeight: 700 }}>
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="card card-pad">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                {/* Search */}
                <div>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>Search</label>
                  <div style={{ position: 'relative' }}>
                    <Search style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--text-faint)' }} />
                    <input
                      type="text"
                      placeholder="Search logs…"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      style={{ width: '100%', paddingLeft: 28, paddingRight: 8, paddingTop: 6, paddingBottom: 6, fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg-0)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Edit Type */}
                <div>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>Edit Type</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={filters.editType}
                      onChange={(e) => handleFilterChange('editType', e.target.value)}
                      style={{ width: '100%', padding: '6px 28px 6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg-0)', color: 'var(--text)', appearance: 'none', outline: 'none', boxSizing: 'border-box' }}
                    >
                      <option value="">All Types</option>
                      {metadata?.editTypes?.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--text-faint)', pointerEvents: 'none' }} />
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>Start Date</label>
                  <ModernCalendar
                    value={filters.startDate ? `${filters.startDate}T00:00:00` : ''}
                    onChange={(iso) => handleFilterChange('startDate', iso ? iso.slice(0, 10) : '')}
                    placeholder="Select start date"
                    includeTime={false}
                  />
                </div>

                {/* End Date */}
                <div>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>End Date</label>
                  <ModernCalendar
                    value={filters.endDate ? `${filters.endDate}T00:00:00` : ''}
                    onChange={(iso) => handleFilterChange('endDate', iso ? iso.slice(0, 10) : '')}
                    placeholder="Select end date"
                    includeTime={false}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                {activeFilterCount > 0 && (
                  <button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <X style={{ width: 13, height: 13 }} />
                    Clear filters
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters(false)}>
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {isLoading || (viewMode === 'stats' && statsLoading) ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '48px 0', color: 'var(--text-muted)' }}>
          <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: 13 }}>Loading edit logs…</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {viewMode === 'timeline' && (
            <>
              <TimelineView logs={logsData?.logs} />
              <Pagination
                pagination={logsData?.pagination}
                onPageChange={handlePageChange}
                limit={filters.limit}
                onLimitChange={handleLimitChange}
              />
            </>
          )}

          {viewMode === 'list' && (
            <>
              <ListView logs={logsData?.logs} />
              <Pagination
                pagination={logsData?.pagination}
                onPageChange={handlePageChange}
                limit={filters.limit}
                onLimitChange={handleLimitChange}
              />
            </>
          )}

          {viewMode === 'stats' && (
            <StatsView stats={statsData} />
          )}
        </div>
      )}

      {/* Log Detail Modal */}
      <AnimatePresence>
        {selectedLog && (
          <LogDetailModal
            log={selectedLog}
            onClose={handleCloseModal}
            metadata={metadata}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Log Detail Modal ──────────────────────────────────────────────────────────

const LogDetailModal = ({ log, onClose, metadata }) => {
  const Icon = getEditTypeIcon(log.edit_type)
  const iconStyle = getEditTypeStyle(log.edit_type)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', margin: 0 }}
      onClick={onClose}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16 }}>
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{ position: 'relative', width: '100%', maxWidth: 680 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="card" style={{ overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
            {/* Modal header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ ...iconStyle, width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon style={{ width: 18, height: 18 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Edit Log Details</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{log.description}</div>
              </div>
              <button
                className="btn-icon"
                onClick={onClose}
                style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Basic + Technical info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Basic Information</h3>
                  {[
                    ['Edit Type', getEditTypeLabel(log.edit_type, metadata)],
                    ['User',      log.user_name],
                    ['Site',      log.site_name],
                    ['Timestamp', new Date(log.created_at).toLocaleString()],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', background: 'var(--bg-0)', borderRadius: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>{val}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Technical Details</h3>
                  {log.ip_address && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', background: 'var(--bg-0)', borderRadius: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)' }}>IP Address</span>
                      <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{log.ip_address}</span>
                    </div>
                  )}
                  {log.session_id && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', background: 'var(--bg-0)', borderRadius: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', flexShrink: 0 }}>Session ID</span>
                      <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{log.session_id}</span>
                    </div>
                  )}
                  {log.before_hash && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', background: 'var(--bg-0)', borderRadius: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', flexShrink: 0 }}>Before Hash</span>
                      <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{log.before_hash.substring(0, 12)}…</span>
                    </div>
                  )}
                  {log.after_hash && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', background: 'var(--bg-0)', borderRadius: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', flexShrink: 0 }}>After Hash</span>
                      <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{log.after_hash.substring(0, 12)}…</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Affected elements */}
              {log.affected_elements?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Affected Elements</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {log.affected_elements.map((el, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--bg-0)', borderRadius: 8 }}>
                        <Target style={{ width: 13, height: 13, color: 'var(--text-faint)', flexShrink: 0 }} />
                        <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{el}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diff data */}
              {log.diff_data && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Changes Overview</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ padding: '12px 16px', background: 'rgba(34,197,94,0.07)', borderRadius: 10, textAlign: 'center' }}>
                      <p style={{ fontSize: 22, fontWeight: 700, color: '#22c55e', margin: 0 }}>+{log.diff_data.stats?.charactersAdded || 0}</p>
                      <p style={{ fontSize: 11.5, color: '#22c55e', margin: '3px 0 0' }}>Characters Added</p>
                    </div>
                    <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.07)', borderRadius: 10, textAlign: 'center' }}>
                      <p style={{ fontSize: 22, fontWeight: 700, color: '#ef4444', margin: 0 }}>-{log.diff_data.stats?.charactersRemoved || 0}</p>
                      <p style={{ fontSize: 11.5, color: '#ef4444', margin: '3px 0 0' }}>Characters Removed</p>
                    </div>
                  </div>

                  {(log.diff_data.added?.length > 0 || log.diff_data.removed?.length > 0 || log.diff_data.modified?.length > 0) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {log.diff_data.added?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <h4 style={{ fontSize: 12, fontWeight: 600, color: '#22c55e', margin: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Plus style={{ width: 13, height: 13 }} /> Added ({log.diff_data.added.length})
                          </h4>
                          <div style={{ maxHeight: 110, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {log.diff_data.added.map((line, i) => (
                              <div key={i} style={{ fontSize: 11.5, background: 'rgba(34,197,94,0.07)', padding: '4px 8px', borderRadius: 5, fontFamily: 'var(--font-mono)', overflowX: 'auto' }}>
                                Line {line.line}: {line.content}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {log.diff_data.removed?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <h4 style={{ fontSize: 12, fontWeight: 600, color: '#ef4444', margin: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Trash2 style={{ width: 13, height: 13 }} /> Removed ({log.diff_data.removed.length})
                          </h4>
                          <div style={{ maxHeight: 110, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {log.diff_data.removed.map((line, i) => (
                              <div key={i} style={{ fontSize: 11.5, background: 'rgba(239,68,68,0.07)', padding: '4px 8px', borderRadius: 5, fontFamily: 'var(--font-mono)', overflowX: 'auto' }}>
                                Line {line.line}: {line.content}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {log.diff_data.modified?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', margin: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Edit style={{ width: 13, height: 13 }} /> Modified ({log.diff_data.modified.length})
                          </h4>
                          <div style={{ maxHeight: 110, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {log.diff_data.modified.map((line, i) => (
                              <div key={i} style={{ fontSize: 11.5, background: 'rgba(108,99,255,0.06)', padding: '4px 8px', borderRadius: 5, fontFamily: 'var(--font-mono)', overflowX: 'auto' }}>
                                <div style={{ color: '#ef4444' }}>- Line {line.line}: {line.before}</div>
                                <div style={{ color: '#22c55e' }}>+ Line {line.line}: {line.after}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional data (JSON) */}
              {log.changes && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Additional Data</h3>
                  <div style={{ background: 'var(--bg-0)', borderRadius: 8, padding: 12, overflowX: 'auto', maxHeight: 192, overflowY: 'auto' }}>
                    <pre style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', margin: 0 }}>
                      {JSON.stringify(log.changes, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default EditLogsDashboard
